<template>
  <div class="p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800">{{ $t('Confirmer les retours') }}</h1>
        <p class="text-gray-500">{{ $t('Validez la reception physique des colis retournes') }}</p>
      </div>
      <router-link
        to="/stockist-dashboard"
        class="px-4 py-2 border rounded-lg hover:bg-gray-100"
      >
        {{ $t('Retour') }}
      </router-link>
    </div>

    <!-- Stats Summary -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div class="bg-orange-50 rounded-lg p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-orange-100 text-orange-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('En attente de validation') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ paginate.total }}</p>
          </div>
        </div>
      </div>
      <div class="bg-green-50 rounded-lg p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Selectionnes') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ selectedReturns.length }}</p>
          </div>
        </div>
      </div>
      <div class="bg-blue-50 rounded-lg p-4">
        <button
          @click="validateSelected"
          :disabled="selectedReturns.length === 0 || validating"
          class="w-full h-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span v-if="validating">
            <svg class="animate-spin h-5 w-5 inline mr-2" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            {{ $t('Validation...') }}
          </span>
          <span v-else>
            {{ $t('Valider la reception') }} ({{ selectedReturns.length }})
          </span>
        </button>
      </div>
    </div>

    <!-- Search -->
    <div class="bg-white rounded-lg shadow p-4 mb-6">
      <div class="flex flex-wrap gap-4 items-center">
        <div class="flex-1 min-w-[200px]">
          <input
            type="text"
            v-model="searchQuery"
            @input="debounceSearch"
            :placeholder="$t('Rechercher par tracking ou telephone...')"
            class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <label class="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            v-model="filters.ignoreCountry"
            @change="loadReturns"
            class="w-4 h-4"
          />
          {{ $t('Ignorer pays') }}
        </label>
        <label class="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            v-model="filters.showAllStatuses"
            @change="loadReturns"
            class="w-4 h-4"
          />
          {{ $t('Tous statuts (debug)') }}
        </label>
        <button
          @click="loadReturns"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {{ $t('Actualiser') }}
        </button>
      </div>
      <!-- Debug info -->
      <div v-if="debugInfo" class="mt-3 p-2 bg-gray-100 rounded text-xs text-gray-600">
        <strong>Debug:</strong> Query: {{ JSON.stringify(debugInfo) }}
      </div>
    </div>

    <!-- Returns Table -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="p-4 border-b flex justify-between items-center">
        <h3 class="text-lg font-semibold">{{ $t('Retours a valider') }}</h3>
        <div class="flex items-center gap-2">
          <input
            type="checkbox"
            @change="toggleSelectAll"
            :checked="selectedReturns.length === returns.length && returns.length > 0"
            class="w-4 h-4"
          />
          <span class="text-sm text-gray-500">{{ $t('Tout selectionner') }}</span>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"></th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ $t('Tracking') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ $t('Client') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ $t('Livreur') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ $t('Vendeur') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ $t('Date retour') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{{ $t('Actions') }}</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="loading">
              <td colspan="7" class="px-4 py-8 text-center">
                <div class="flex justify-center items-center">
                  <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span class="ml-2">{{ $t('Chargement...') }}</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="returns.length === 0">
              <td colspan="7" class="px-4 py-8 text-center text-gray-500">
                <svg class="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                {{ $t('Aucun retour en attente de validation') }}
              </td>
            </tr>
            <tr
              v-else
              v-for="ret in returns"
              :key="ret._id"
              class="hover:bg-gray-50 cursor-pointer"
              :class="{ 'bg-green-50': isSelected(ret._id) }"
              @click="toggleSelect(ret._id)"
            >
              <td class="px-4 py-3">
                <input
                  type="checkbox"
                  :checked="isSelected(ret._id)"
                  @click.stop="toggleSelect(ret._id)"
                  class="w-4 h-4"
                />
              </td>
              <td class="px-4 py-3">
                <div class="text-sm font-medium text-blue-600">{{ ret.trackingNumber }}</div>
                <div class="text-xs text-gray-500">{{ getOrderId(ret) }}</div>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm font-medium text-gray-900">{{ getCustomerName(ret) }}</div>
                <div class="text-xs text-gray-500">{{ getCustomerPhone(ret) }}</div>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm text-gray-900">{{ getCourierName(ret) }}</div>
                <div class="text-xs text-gray-500">{{ getCourierPhone(ret) }}</div>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm text-gray-900">{{ getSellerName(ret) }}</div>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm text-gray-900">{{ formatDate(ret.updatedAt) }}</div>
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click.stop="validateSingle(ret)"
                  class="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  {{ $t('Valider') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
        <div class="text-sm text-gray-700">
          {{ $t('Affichage') }} {{ returns.length }} {{ $t('sur') }} {{ paginate.total }} {{ $t('resultats') }}
        </div>
        <div class="flex gap-2">
          <button
            @click="changePage('prev')"
            :disabled="paginate.currentpage <= 1"
            class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            {{ $t('Precedent') }}
          </button>
          <span class="px-3 py-1 text-sm">
            {{ paginate.currentpage }} / {{ paginate.lastpage }}
          </span>
          <button
            @click="changePage('next')"
            :disabled="paginate.currentpage >= paginate.lastpage"
            class="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            {{ $t('Suivant') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showConfirmModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">{{ $t('Confirmer la validation') }}</h3>
        <p class="text-gray-600 mb-4">
          {{ $t('Vous allez valider la reception physique de') }} <strong>{{ selectedReturns.length }}</strong> {{ $t('colis.') }}
        </p>
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('Commentaire (optionnel)') }}</label>
          <textarea
            v-model="validationComment"
            rows="2"
            class="w-full px-3 py-2 border rounded-lg"
            :placeholder="$t('Ajouter un commentaire...')"
          ></textarea>
        </div>
        <div class="flex justify-end gap-3">
          <button
            @click="showConfirmModal = false"
            class="px-4 py-2 border rounded-lg hover:bg-gray-100"
          >
            {{ $t('Annuler') }}
          </button>
          <button
            @click="confirmValidation"
            :disabled="validating"
            class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {{ $t('Confirmer') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div v-if="showSuccessModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <div class="text-center mb-4">
          <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg class="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mt-4">{{ $t('Validation reussie') }}</h3>
          <p class="text-gray-500 mt-2">{{ validationResult.message }}</p>
        </div>
        <button
          @click="closeSuccessModal"
          class="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {{ $t('Fermer') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConfirmReturns',
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      loading: false,
      validating: false,
      returns: [],
      selectedReturns: [],
      searchQuery: '',
      filters: {
        ignoreCountry: false,
        showAllStatuses: false
      },
      paginate: {
        total: 0,
        currentpage: 1,
        lastpage: 1,
        per_page: 50
      },
      showConfirmModal: false,
      showSuccessModal: false,
      validationComment: '',
      validationResult: {},
      searchTimeout: null,
      debugInfo: null
    };
  },
  async mounted() {
    if (this.currentUser && this.currentUser._id) {
      await this.loadReturns();
    }
  },
  watch: {
    // Reload when warehouse changes in header
    idWharhouse: {
      handler: function() {
        this.loadReturns();
      },
      deep: true
    }
  },
  methods: {
    async loadReturns() {
      this.loading = true;
      this.debugInfo = null;
      try {
        const params = {
          page: this.paginate.currentpage,
          limit: this.paginate.per_page
        };

        // Use selected country from header
        if (this.idWharhouse && this.idWharhouse.country && !this.filters.ignoreCountry) {
          params.country = this.idWharhouse.country;
        }

        if (this.searchQuery) {
          params.search = this.searchQuery;
        }

        // Debug filters
        if (this.filters.ignoreCountry) {
          params.ignoreCountry = true;
          params.ignoreWarehouse = true;
        }
        if (this.filters.showAllStatuses) {
          params.showAllStatuses = true;
        }

        console.log('loadReturns params:', params);

        const res = await this.$server.getPendingReturns('stockistDashboard', params);
        console.log('loadReturns response:', res);

        if (res && res.content) {
          this.returns = res.content.results || [];
          this.paginate.total = res.content.total || 0;
          this.paginate.lastpage = res.content.last_page || 1;
          this.debugInfo = res.content.query || null;
        } else if (res && res.results) {
          this.returns = res.results || [];
          this.paginate.total = res.total || 0;
          this.paginate.lastpage = res.last_page || 1;
          this.debugInfo = res.query || null;
        }
      } catch (error) {
        console.error('Error loading returns:', error);
        this.returns = [];
      }
      this.loading = false;
    },

    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(function() {
        this.paginate.currentpage = 1;
        this.loadReturns();
      }.bind(this), 500);
    },

    changePage(direction) {
      if (direction === 'next' && this.paginate.currentpage < this.paginate.lastpage) {
        this.paginate.currentpage++;
      } else if (direction === 'prev' && this.paginate.currentpage > 1) {
        this.paginate.currentpage--;
      }
      this.loadReturns();
    },

    isSelected(id) {
      return this.selectedReturns.includes(id);
    },

    toggleSelect(id) {
      const index = this.selectedReturns.indexOf(id);
      if (index === -1) {
        this.selectedReturns.push(id);
      } else {
        this.selectedReturns.splice(index, 1);
      }
    },

    toggleSelectAll() {
      if (this.selectedReturns.length === this.returns.length) {
        this.selectedReturns = [];
      } else {
        this.selectedReturns = this.returns.map(r => r._id);
      }
    },

    validateSelected() {
      if (this.selectedReturns.length === 0) return;
      this.showConfirmModal = true;
    },

    validateSingle(ret) {
      this.selectedReturns = [ret._id];
      this.showConfirmModal = true;
    },

    async confirmValidation() {
      this.validating = true;
      try {
        const res = await this.$server.validateReturn('stockistDashboard', {
          shippingIds: this.selectedReturns,
          comment: this.validationComment
        });

        if (res && (res.content || res.message)) {
          this.validationResult = res.content || res;
          this.showConfirmModal = false;
          this.showSuccessModal = true;
          this.selectedReturns = [];
          this.validationComment = '';
          await this.loadReturns();
        }
      } catch (error) {
        console.error('Error validating returns:', error);
        alert(this.$t('Erreur lors de la validation'));
      }
      this.validating = false;
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.validationResult = {};
    },

    formatDate(date) {
      if (!date) return '-';
      return this.$moment(date).format('DD/MM/YYYY HH:mm');
    },

    getOrderId(ret) {
      return ret && ret.orderObjet ? ret.orderObjet.id : '';
    },

    getCustomerName(ret) {
      if (ret && ret.orderObjet && ret.orderObjet.customer) {
        return ret.orderObjet.customer.fullName || '';
      }
      return '';
    },

    getCustomerPhone(ret) {
      if (ret && ret.orderObjet && ret.orderObjet.customer) {
        return ret.orderObjet.customer.phone || '';
      }
      return '';
    },

    getCourierName(ret) {
      return ret && ret.courier ? ret.courier.fullName : '-';
    },

    getCourierPhone(ret) {
      return ret && ret.courier ? ret.courier.phone : '';
    },

    getSellerName(ret) {
      return ret && ret.seller ? ret.seller.fullName : '-';
    }
  }
};
</script>

<style scoped>
</style>
